<template>
  <div class="max-w-[1280px] mx-auto my-8 px-6">
    <div class="flex justify-between items-center mb-8">
      <h1 class="font-[Poppins] font-bold text-2xl text-[#191c1e]">
        Kategorier
      </h1>
      <Button as="a" :href="newCategoryPath">Ny kategori</Button>
    </div>

    <!-- Page config: hero + banner images -->
    <div class="rounded-2xl border border-[#e6e8ea] p-6 mb-8">
      <h2 class="font-[Poppins] font-bold text-lg text-[#191c1e] mb-4">
        Kategorisidans inställningar
      </h2>
      <form @submit.prevent="submitPageConfig">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Hero image -->
          <div>
            <Label class="mb-2 block">Hero-bild</Label>
            <p class="text-sm text-[#6c7a77] mb-3">
              Visas i toppen av kategorisidan. Utan bild visas en teal-gradient.
            </p>
            <div v-if="pageConfig.hero_image_url" class="mb-3">
              <img
                :src="pageConfig.hero_image_url"
                alt="Hero-bild"
                class="h-32 w-auto rounded-lg object-cover"
              />
            </div>
            <div
              v-else
              class="h-32 w-48 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 mb-3 flex items-center justify-center text-white/60 text-sm"
            >
              Ingen bild
            </div>
            <Input
              type="file"
              accept="image/*"
              @change="
                (e) => (configForm.hero_image = e.target.files[0] ?? null)
              "
            />
          </div>

          <!-- Banner image -->
          <div>
            <Label class="mb-2 block">Bannerbild (Instagram-sektion)</Label>
            <p class="text-sm text-[#6c7a77] mb-3">
              Visas i Instagram-bannern längst ner. Utan bild visas en
              teal-gradient.
            </p>
            <div v-if="pageConfig.banner_image_url" class="mb-3">
              <img
                :src="pageConfig.banner_image_url"
                alt="Bannerbild"
                class="h-32 w-auto rounded-lg object-cover"
              />
            </div>
            <div
              v-else
              class="h-32 w-48 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 mb-3 flex items-center justify-center text-white/60 text-sm"
            >
              Ingen bild
            </div>
            <Input
              type="file"
              accept="image/*"
              @change="
                (e) => (configForm.banner_image = e.target.files[0] ?? null)
              "
            />
          </div>
        </div>
        <div class="mt-4">
          <Button type="submit" :disabled="configForm.processing" size="sm">
            Spara inställningar
          </Button>
        </div>
      </form>
    </div>

    <!-- Position legend -->
    <div class="rounded-2xl border border-[#e6e8ea] p-4 mb-6 bg-[#f9fafb]">
      <h3 class="font-[Poppins] font-semibold text-sm text-[#191c1e] mb-2">
        Positioner på kategorisidan
      </h3>
      <div class="text-[13px] text-[#6c7a77] space-y-1">
        <p>
          <span class="font-semibold text-[#191c1e]">1</span> — Stor bild
          (vänster)
        </p>
        <p>
          <span class="font-semibold text-[#191c1e]">2</span> — Mellan-bild
          (höger topp)
        </p>
        <p>
          <span class="font-semibold text-[#191c1e]">3–4</span> — Två små bilder
          (höger botten)
        </p>
        <p>
          <span class="font-semibold text-[#191c1e]">5–6</span> — Horisontella
          kort med bild
        </p>
        <p>
          <span class="font-semibold text-[#191c1e]">7–9</span> — Fler
          kategorier (bildkort, tre i rad)
        </p>
        <p>
          <span class="font-semibold text-[#191c1e]">—</span> — Ingen position =
          listkort längst ner
        </p>
      </div>
    </div>

    <!-- Categories table -->
    <div class="rounded-2xl border border-[#e6e8ea] overflow-hidden">
      <table class="w-full text-sm">
        <thead
          class="bg-[#f2f4f6] text-[#6c7a77] text-[12px] uppercase tracking-wide font-semibold"
        >
          <tr>
            <th class="text-left px-4 py-3 w-12"></th>
            <th class="text-left px-4 py-3">Namn</th>
            <th class="text-left px-4 py-3 hidden sm:table-cell">Undertitel</th>
            <th class="text-left px-4 py-3 w-24">Position</th>
            <th class="text-left px-4 py-3 hidden md:table-cell">
              Underkategorier
            </th>
            <th class="text-left px-4 py-3 w-8">Färg</th>
            <th class="px-4 py-3 w-24"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-[#e6e8ea]">
          <tr
            v-for="category in categories"
            :key="category.id"
            class="hover:bg-[#f9fafb] transition-colors"
          >
            <!-- Thumbnail -->
            <td class="px-4 py-3">
              <div
                class="w-10 h-10 rounded-lg overflow-hidden bg-[#f2f4f6] shrink-0"
              >
                <img
                  v-if="category.cover_image_url"
                  :src="category.cover_image_url"
                  :alt="category.name"
                  class="w-full h-full object-cover"
                />
              </div>
            </td>
            <!-- Name -->
            <td class="px-4 py-3">
              <Link
                :href="category.path"
                class="font-[Poppins] font-bold text-[14px] text-[#191c1e] hover:text-teal-600 transition-colors"
                >{{ category.name }}</Link
              >
            </td>
            <!-- Subtitle -->
            <td class="px-4 py-3 hidden sm:table-cell text-[#6c7a77]">
              {{ category.subtitle || "—" }}
            </td>
            <!-- Position dropdown -->
            <td class="px-4 py-3">
              <select
                :value="category.position ?? ''"
                class="text-sm rounded-md border border-[#e6e8ea] bg-white px-2 py-1 focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                @change="updatePosition(category, $event)"
              >
                <option value="">—</option>
                <option
                  v-for="pos in 9"
                  :key="pos"
                  :value="pos"
                  :disabled="isPositionTaken(pos, category.id)"
                >
                  {{ pos
                  }}{{ isPositionTaken(pos, category.id) ? " (upptagen)" : "" }}
                </option>
              </select>
            </td>
            <!-- Subcategories -->
            <td class="px-4 py-3 hidden md:table-cell">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="child in category.children"
                  :key="child.id"
                  class="px-2 py-0.5 rounded-full bg-[#f2f4f6] text-[11px] text-[#3c4947]"
                  >{{ child.name }}</span
                >
                <span
                  v-if="category.children.length === 0"
                  class="text-[#d0d5d4]"
                  >—</span
                >
              </div>
            </td>
            <!-- Color dot -->
            <td class="px-4 py-3">
              <span
                v-if="category.color"
                class="inline-block w-4 h-4 rounded-full border border-white shadow"
                :style="{ backgroundColor: category.color }"
              />
              <span v-else class="text-[#d0d5d4]">—</span>
            </td>
            <!-- Edit -->
            <td class="px-4 py-3 text-right">
              <Link
                :href="category.edit_path"
                class="text-[12px] text-teal-600 font-semibold hover:underline whitespace-nowrap"
                >Redigera</Link
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { Link, useForm, router } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const props = defineProps({
  categories: { type: Array, default: () => [] },
  currentUser: { type: Object, default: null },
  newCategoryPath: { type: String, default: "" },
  adminCategoriesPath: { type: String, default: "" },
  pageConfig: { type: Object, default: () => ({}) },
  pageConfigPath: { type: String, default: "" },
});

const configForm = useForm({
  hero_image: null,
  banner_image: null,
});

function submitPageConfig() {
  configForm.patch(props.pageConfigPath, {
    forceFormData: true,
  });
}

function isPositionTaken(pos, currentId) {
  return props.categories.some((c) => c.position === pos && c.id !== currentId);
}

function updatePosition(category, event) {
  const newPosition =
    event.target.value === "" ? null : parseInt(event.target.value);
  router.patch(
    category.edit_path.replace("/edit", ""),
    { category: { position: newPosition } },
    { preserveScroll: true },
  );
}
</script>
